import axios from 'axios';

const MSG = {
  cannotReachServer: 'Cannot reach the server',
  cannotReachDatabase: 'Cannot reach the database',
  timedOut: 'Request timed out — try again',
  certFailed: 'Secure connection failed (certificate)',
  serviceUnavailable: 'Service temporarily unavailable',
  serverError: 'Something went wrong on the server',
  tryAgainLater: 'Something went wrong — try again later',
  requestFailed: 'Request failed — try again',
  sessionExpired: 'Session expired — please log in',
  wrongEmailOrPassword: 'Wrong email or password',
  invalidCode: 'Invalid code',
  codeExpired: 'Code expired — request a new one',
  couldNotSendEmail: 'Could not send email — try again later',
  currentPasswordIncorrect: 'Current password is incorrect',
  invalidEmailOrOtp: 'Invalid email or code',
} as const;

function textFromUnknown(error: unknown): string {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message || '';
  const anyErr = error as {
    message?: string;
    code?: string;
    toString?: () => string;
  };
  return `${anyErr.message || ''} ${anyErr.code || ''} ${anyErr.toString?.() || ''}`;
}

/** Map Axios / network / SSL failures to short user text. */
export function getUserFacingError(error: unknown): string {
  const raw = textFromUnknown(error).toLowerCase();

  if (
    /cert_has_expired|unable_to_verify|certificate|ssl|tls|err_cert|cert_authority_invalid|cert_common_name_invalid/.test(
      raw
    )
  ) {
    return MSG.certFailed;
  }

  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const code = (error.code || '').toUpperCase();
    const msg = (error.message || '').toLowerCase();

    if (!error.response) {
      if (code === 'ECONNABORTED' || msg.includes('timeout')) {
        return MSG.timedOut;
      }
      if (
        code === 'ECONNREFUSED' ||
        code === 'ENOTFOUND' ||
        code === 'ERR_NETWORK' ||
        msg.includes('network error')
      ) {
        return MSG.cannotReachServer;
      }
      if (/cert|ssl|tls|certificate/.test(msg + code.toLowerCase())) {
        return MSG.certFailed;
      }
      return MSG.cannotReachServer;
    }

    if (status === 502 || status === 503 || status === 504) {
      const bodyMsg = error.response.data?.message;
      if (typeof bodyMsg === 'string' && bodyMsg.trim()) {
        return getAuthBodyMessage(error.response.data);
      }
      return MSG.serviceUnavailable;
    }

    if (status === 401 || status === 403) {
      const body = error.response.data;
      if (body && (body.message || body.detail)) {
        return getAuthBodyMessage(body);
      }
      return MSG.sessionExpired;
    }

    if (status === 500) {
      const bodyMsg = error.response.data?.message;
      if (typeof bodyMsg === 'string' && bodyMsg.trim() && !bodyMsg.startsWith('Error:')) {
        return getAuthBodyMessage(error.response.data);
      }
      return MSG.serverError;
    }

    const detail = error.response.data?.detail;
    const message = error.response.data?.message;
    if (typeof message === 'string' && message.trim()) {
      return getAuthBodyMessage(error.response.data);
    }
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }

  if (/econnrefused|enotfound|network error|cannot reach/.test(raw)) {
    return MSG.cannotReachServer;
  }
  if (/timeout|econnaborted/.test(raw)) {
    return MSG.timedOut;
  }

  return MSG.requestFailed;
}

/**
 * Remap known auth/password API body messages (including legacy strings)
 * to the short catalog. Prefer remapped text when recognizable.
 */
export function getAuthBodyMessage(data: any): string {
  if (!data) return MSG.requestFailed;
  const status = data.status_code;
  const message = typeof data.message === 'string' ? data.message.trim() : '';
  const lower = message.toLowerCase();

  if (
    lower === 'invalid credentials' ||
    lower.includes('wrong email or password')
  ) {
    return MSG.wrongEmailOrPassword;
  }
  if (lower === 'invalid otp' || lower === 'invalid code') {
    return MSG.invalidCode;
  }
  if (lower === 'otp expired' || lower.includes('code expired')) {
    return MSG.codeExpired;
  }
  if (
    lower.includes('unable to send otp') ||
    lower.includes('could not send email')
  ) {
    return MSG.couldNotSendEmail;
  }
  if (lower === 'current password is incorrect') {
    return MSG.currentPasswordIncorrect;
  }
  if (lower === 'invalid email or otp' || lower === 'invalid email or code') {
    return MSG.invalidEmailOrOtp;
  }
  if (
    lower.includes('server connection') ||
    lower.includes('cannot reach the database') ||
    lower.includes('cannot reach the server')
  ) {
    if (lower.includes('database')) return MSG.cannotReachDatabase;
    return MSG.cannotReachServer;
  }
  if (
    lower.startsWith('error:') ||
    lower.includes('server side error') ||
    lower === 'service unavailable'
  ) {
    return MSG.tryAgainLater;
  }

  if (status === 503) {
    return message || MSG.serviceUnavailable;
  }
  if (status === 500 && (!message || message.startsWith('Error:'))) {
    return MSG.tryAgainLater;
  }

  if (message) return message;
  return MSG.requestFailed;
}

export const USER_FACING_MSG = MSG;
